import React, { useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useConfiguration } from '../../context/configurationContext';
import {
  NamedLink,
  IconSpinner,
  Icons,
  AspectRatioWrapper,
  ResponsiveImage,
} from '../../components';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { ensureListing } from '../../util/data';
import { capitalizeFirstLetter } from '../../util/genericHelpers';
import {
  currentCartItemIdSelector,
  addToCartErrorSelector,
} from './CartPage.duck';
import { removeItemFromCart } from '../../util/cartHelpers';
import { selectIsAuthenticated } from '../../ducks/auth.duck';
import css from './CartPage.module.css';
import { createSlug } from '../../util/urlHelpers';
import { richText } from '../../util/richText';
import { formatMoney } from '../../util/currency';
import { getUserCartDetails } from '../../util/dataExtractor';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const CartPanel = ({
  className,
  rootClassName,
  listing,
  renderSizes,
  currentUser,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const config = useConfiguration();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const addToCartError = useSelector(addToCartErrorSelector);
  const currentCartItemId = useSelector(currentCartItemIdSelector);
  const cartItems = getUserCartDetails(currentUser);

  const [isHovered, setHovered] = useState(false);

  const MIN_LENGTH_FOR_LONG_WORDS = 10;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const { title = '', price, publicData } = listing?.attributes || {};
  const firstImage = listing.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage.attributes.variants).filter(k => k.startsWith(variantPrefix))
    : [];
  const slug = createSlug(title);
  const currentListing = ensureListing(listing);
  const id = currentListing?.id?.uuid;

  const priceData = (price, intl) => {
    if (price && price.currency === config.currency) {
      const formattedPrice = formatMoney(intl, price);
      return { formattedPrice, priceTitle: formattedPrice };
    }
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  };

  const { priceTitle } = priceData(price, intl);
  const itemTotal = price ? price.amount / 100 : 0;

  const handleRemoveItem = () => {
    removeItemFromCart({ isAuthenticated, id, dispatch, cartItems, config });
  };

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      {addToCartError && (
        <div className={css.error}>
          <FormattedMessage id="CartPage.addToCartError" />
        </div>
      )}
      <div className={css.productDetails}>
        <div className={css.productDetailsLeft}>
          <div className={css.productImg}>
            <NamedLink name="ListingPage" params={{ id, slug }}>
              <AspectRatioWrapper
                className={css.aspectRatioWrapper}
                width={aspectWidth}
                height={aspectHeight}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
              >
                <LazyImage
                  rootClassName={css.rootForImage}
                  alt={title}
                  image={firstImage}
                  variants={variants}
                  sizes={renderSizes}
                />
              </AspectRatioWrapper>
            </NamedLink>
          </div>
          <div className={css.info}>
            <div className={css.mainInfo}>
              <div className={css.title}>
                <NamedLink name="ListingPage" params={{ id, slug }}>
                  {richText(title, {
                    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                    longWordClass: css.longWord,
                  })}
                </NamedLink>
              </div>

              {publicData?.brand && (
                <div className={css.authorInfo}> {capitalizeFirstLetter(publicData.brand)} </div>
              )}
            </div>
            <div className={css.removeListing} onClick={handleRemoveItem}>
              {currentCartItemId === id ? (
                <IconSpinner />
              ) : (
                <>
                  <Icons name="trash" /> <FormattedMessage id="CartPage.removeItem" />
                </>
              )}
            </div>
          </div>
        </div>
        <div className={css.productDetailsRight}>
          <div className={css.stock}>
            <span className={css.stockCount}>1</span>
          </div>
        </div>
        <div className={css.price}>
          <div className={css.priceValue} title={priceTitle}>
            £{itemTotal.toFixed(2)}
          </div>
          {config.listing.showUnitTypeTranslations && (
            <div className={css.perUnit}>
              <FormattedMessage id="CartPage.perUnit" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartPanel;
